'use client';

import { type ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import { type FC, useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';

const VALID_UTM_VALUES = {
  SOURCE: 'source',
  CAMPAIGN: 'campaign',
  MEDIUM: 'medium',
} as const;

type ValidUTMValuesT = (typeof VALID_UTM_VALUES)[keyof typeof VALID_UTM_VALUES];

const UTMKeyPrefix = 'utm';

export type UTMKey = `${typeof UTMKeyPrefix}_${ValidUTMValuesT}`;

export const SESSION_KEY = 'utm-query-params';

const UTM_PARAM_KEYS: Record<ValidUTMValuesT, UTMKey> = {
  [VALID_UTM_VALUES.SOURCE]: `${UTMKeyPrefix}_${VALID_UTM_VALUES.SOURCE}`,
  [VALID_UTM_VALUES.MEDIUM]: `${UTMKeyPrefix}_${VALID_UTM_VALUES.MEDIUM}`,
  [VALID_UTM_VALUES.CAMPAIGN]: `${UTMKeyPrefix}_${VALID_UTM_VALUES.CAMPAIGN}`,
} as const;

const prepareUTMStringFromSearchParams = (
  searchParams: ReadonlyURLSearchParams,
  keys: UTMKey[],
) => {
  let utmQueryParamsString = '';

  for (const key of keys) {
    const value = searchParams.get(key);

    if (!value) continue;

    utmQueryParamsString += `${key}=${value}&`;
  }

  return utmQueryParamsString.slice(0, -1);
};

export const UTMCapture: FC = () => {
  const params = useSearchParams();

  const [utmValues, setUTMValues] = useSessionStorage<string>(SESSION_KEY, '');

  const preparedUTMString = prepareUTMStringFromSearchParams(
    params,
    Object.values(UTM_PARAM_KEYS),
  );

  useEffect(() => {
    if (utmValues) return;

    setUTMValues(preparedUTMString);
  }, [utmValues, setUTMValues, preparedUTMString]);

  return null;
};
